import React, { useMemo, useRef, useState } from 'react';
import Papa from 'papaparse';
import { supabase } from '../supabaseClient';
import { useAuth } from '../auth/AuthContext';

const REQUIRED_HEADERS = ['Student Name', 'EPS ID', 'Student Number', 'Course'];

const initialResult = {
  total: 0,
  added: 0,
  skipped: 0,
  assigned: 0,
  errors: [],
};

function sanitizeRow(row) {
  return {
    name: (row['Student Name'] || '').trim(),
    unique_id: (row['EPS ID'] || '').trim(),
    phone: (row['Student Number'] || '').trim(),
    whatsapp: (row['Student Whatsapp'] || '').trim(),
    parent_number: (row['Parent Number'] || '').trim(),
    course: (row['Course'] || '').trim(),
    campus: (row['Campus'] || '').trim(),
    hostel: (row['Hostel'] || '').trim(),
    created_at: row['Created At'] ? new Date(row['Created At']).toISOString() : new Date().toISOString(),
  };
}

export default function UploadStudents() {
  const { user } = useAuth();
  const [file, setFile] = useState(null);
  const [previewData, setPreviewData] = useState([]);
  const [isImporting, setIsImporting] = useState(false);
  const [result, setResult] = useState(initialResult);
  const [validationErrors, setValidationErrors] = useState([]);
  const [mentorCount, setMentorCount] = useState(null);
  const fileInputRef = useRef(null);

  const canImport = !!file && validationErrors.length === 0;

  const previewHeaders = useMemo(() => {
    if (previewData.length === 0) return [];
    return Object.keys(previewData[0]);
  }, [previewData]);

  const resetState = (options = {}) => {
    const { keepResult = false } = options;
    setFile(null);
    setPreviewData([]);
    setValidationErrors([]);
    setMentorCount(null);
    if (!keepResult) {
      setResult(initialResult);
    }
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleFileChange = (event) => {
    const uploaded = event.target.files?.[0];
    if (!uploaded) return;

    setFile(uploaded);
    setResult(initialResult);
    setValidationErrors([]);

    Papa.parse(uploaded, {
      header: true,
      skipEmptyLines: true,
      complete: ({ data, meta }) => {
        const headers = meta?.fields ?? [];
        const missing = REQUIRED_HEADERS.filter((header) => !headers.includes(header));

        if (missing.length > 0) {
          setValidationErrors([
            `Missing required column${missing.length > 1 ? 's' : ''}: ${missing.join(', ')}`,
          ]);
          setPreviewData([]);
          return;
        }

        const sanitized = data
          .map(sanitizeRow)
          .filter((row) => row.name && row.unique_id && row.phone && row.course);

        if (sanitized.length === 0) {
          setValidationErrors(['No valid rows found in CSV.']);
          setPreviewData([]);
          return;
        }

        setPreviewData(sanitized.slice(0, 5));
        if (sanitized.length !== data.length) {
          setValidationErrors([
            `${data.length - sanitized.length} row(s) missing required data were ignored in preview and will be skipped on import.`,
          ]);
        }
      },
      error: (error) => {
        console.error('Error parsing CSV file', error);
        setValidationErrors(['Unable to parse CSV. Please check the format and try again.']);
        setPreviewData([]);
      },
    });
  };

  const fetchMentorCount = async () => {
    const { count, error } = await supabase.from('mentors').select('*', { count: 'exact', head: true });
    if (error) {
      console.error('Error fetching mentors', error);
      throw new Error('Unable to fetch mentors. Please try again later.');
    }
    return count ?? 0;
  };

  const getMentorAssignments = async (students) => {
    const mentorTotal = mentorCount ?? (await fetchMentorCount());
    setMentorCount(mentorTotal);

    if (mentorTotal === 0) {
      throw new Error('No mentors available. Please create mentors before importing students.');
    }

    const { data: mentors, error: mentorsError } = await supabase
      .from('mentors')
      .select('id, course')
      .order('created_at', { ascending: true });

    if (mentorsError) {
      console.error('Error loading mentors for assignment', mentorsError);
      throw new Error('Unable to load mentors for assignment.');
    }

    if (!mentors || mentors.length === 0) {
      throw new Error('No mentors available. Please create mentors before importing students.');
    }

    // Group mentors by course
    const mentorsByCourse = mentors.reduce((acc, mentor) => {
      // Handle both array and legacy string
      const courses = Array.isArray(mentor.course)
        ? mentor.course
        : (mentor.course ? [mentor.course] : []);

      if (courses.length === 0) return acc;

      courses.forEach(courseName => {
        if (!acc[courseName]) acc[courseName] = [];
        acc[courseName].push(mentor.id);
      });
      return acc;
    }, {});

    // Track assignment pointers for each course
    const pointers = {};

    return students.map((student) => {
      const courseMentors = mentorsByCourse[student.course];
      if (!courseMentors || courseMentors.length === 0) {
        throw new Error(`No mentors available for course: ${student.course}`);
      }

      if (!pointers[student.course]) {
        pointers[student.course] = 0;
      }

      const mentor_id = courseMentors[pointers[student.course] % courseMentors.length];
      pointers[student.course] += 1;
      return { ...student, mentor_id };
    });
  };

  const handleImport = async () => {
    if (!file) return;

    setIsImporting(true);
    setResult(initialResult);

    try {
      const { data, meta, errors: parseErrors } = await new Promise((resolve, reject) => {
        Papa.parse(file, {
          header: true,
          skipEmptyLines: true,
          complete: resolve,
          error: reject,
        });
      });

      if (parseErrors?.length) {
        throw new Error('CSV parsing failed. Please verify the file format.');
      }

      const missing = REQUIRED_HEADERS.filter((header) => !meta?.fields?.includes(header));
      if (missing.length > 0) {
        throw new Error(`Missing required column${missing.length > 1 ? 's' : ''}: ${missing.join(', ')}`);
      }

      const cleaned = data.map(sanitizeRow).filter((row) => row.name && row.unique_id && row.phone && row.course);
      if (cleaned.length === 0) {
        throw new Error('No valid student rows found.');
      }

      const uniqueRows = Array.from(new Map(cleaned.map((row) => [row.unique_id, row])).values());

      const chunkSize = 100;
      let added = 0;
      let skipped = 0;
      let newlyAssigned = 0;
      const errors = [];

      for (let i = 0; i < uniqueRows.length; i += chunkSize) {
        const chunk = uniqueRows.slice(i, i + chunkSize);
        const uniqueIds = chunk.map((student) => student.unique_id);

        const { data: existing, error } = await supabase
          .from('students')
          .select('unique_id')
          .in('unique_id', uniqueIds);

        if (error) {
          console.error('Error checking existing students', error);
          errors.push('Failed to check existing students.');
          continue;
        }

        const existingSet = new Set((existing ?? []).map((item) => item.unique_id));
        const toInsert = chunk.filter((student) => !existingSet.has(student.unique_id));

        if (toInsert.length === 0) {
          skipped += chunk.length;
          continue;
        }

        const withMentors = await getMentorAssignments(toInsert);

        const { error: insertError } = await supabase.from('students').insert(withMentors);

        if (insertError) {
          console.error('Error inserting students', insertError);
          errors.push(`Failed to insert ${toInsert.length} students.`);
        } else {
          added += toInsert.length;
          newlyAssigned += withMentors.length;
        }

        skipped += chunk.length - toInsert.length;

        setResult({
          total: uniqueRows.length,
          added,
          skipped,
          assigned: newlyAssigned,
          errors,
        });
      }

      setResult({
        total: uniqueRows.length,
        added,
        skipped,
        assigned: newlyAssigned,
        errors,
      });

      resetState({ keepResult: true });
    } catch (error) {
      console.error('Import failed', error);
      setResult((prev) => ({
        ...prev,
        errors: [...prev.errors, error.message],
      }));
    } finally {
      setIsImporting(false);
    }
  };

  if (!user) {
    return (
      <div className="mx-auto max-w-3xl rounded-2xl border border-amber-200 bg-amber-50 p-8 text-amber-800 dark:border-amber-400/60 dark:bg-amber-500/10 dark:text-amber-200">
        <h1 className="text-lg font-semibold">Please sign in as an admin</h1>
        <p className="mt-2 text-sm">
          You must be authenticated to import students. Log in with an administrator account and try again.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <header className="space-y-2">
        <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-slate-900 dark:text-white">Import students</h1>
        <p className="text-sm text-slate-600 dark:text-slate-400">
          Upload CSV files with student records. Existing entries (matching unique IDs) will be skipped automatically and new ones assigned to mentors in a round-robin within their course.
        </p>
      </header>

      <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-900">
        <div className="flex flex-col gap-6 lg:flex-row">
          <div className="flex-1 space-y-6">
            <div>
              <h2 className="text-lg font-semibold text-slate-900 dark:text-white">Upload CSV</h2>
              <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                Required columns: <code className="rounded bg-slate-100 px-2 py-1 text-xs dark:bg-slate-800">Student Name</code>,
                <code className="rounded bg-slate-100 px-2 py-1 text-xs dark:bg-slate-800">EPS ID</code>,
                <code className="rounded bg-slate-100 px-2 py-1 text-xs dark:bg-slate-800">Student Number</code>,
                <code className="rounded bg-slate-100 px-2 py-1 text-xs dark:bg-slate-800">Course</code>
              </p>
            </div>

            <div className="rounded-2xl border border-dashed border-slate-300 bg-white px-6 py-10 text-center dark:border-slate-700 dark:bg-slate-950/40">
              <svg
                className="mx-auto h-12 w-12 text-slate-400"
                viewBox="0 0 48 48"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M28 8H12a4 4 0 00-4 4v20" />
                <path d="M40 20v16a4 4 0 01-4 4H12" />
                <path d="M32 24l-8-8-8 8" />
                <path d="M24 16v16" />
              </svg>
              <div className="mt-4 flex items-center justify-center gap-2 text-sm text-slate-600 dark:text-slate-400">
                <label
                  htmlFor="student-upload"
                  className="cursor-pointer rounded-full bg-brand/10 px-3 py-1 text-brand hover:bg-brand/20 dark:bg-brand/20 dark:text-brand-light"
                >
                  Browse files
                  <input
                    id="student-upload"
                    type="file"
                    accept=".csv"
                    onChange={handleFileChange}
                    ref={fileInputRef}
                    className="sr-only"
                  />
                </label>
                <span>or drag and drop</span>
              </div>
              <p className="mt-2 text-xs text-slate-500 dark:text-slate-500">CSV up to 5MB</p>
              {file && (
                <p className="mt-4 text-sm font-medium text-slate-700 dark:text-slate-200">
                  Selected: {file.name} ({Math.round(file.size / 1024)} KB)
                </p>
              )}
            </div>

            {validationErrors.length > 0 && (
              <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-700 dark:border-amber-400/40 dark:bg-amber-500/10 dark:text-amber-200">
                <ul className="list-disc space-y-1 pl-4">
                  {validationErrors.map((msg, index) => (
                    <li key={index}>{msg}</li>
                  ))}
                </ul>
              </div>
            )}

            <div className="flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={resetState}
                disabled={isImporting}
                className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-600 shadow-sm hover:text-brand hover:border-brand/40 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-300"
              >
                Reset
              </button>
              <button
                type="button"
                onClick={handleImport}
                disabled={!canImport || isImporting}
                className="inline-flex items-center gap-2 rounded-xl bg-brand px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-brand-dark focus:outline-none focus:ring-2 focus:ring-brand/50 disabled:opacity-60"
              >
                {isImporting ? (
                  <>
                    <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    Importing…
                  </>
                ) : (
                  'Import students'
                )}
              </button>
            </div>
          </div>

          <div className="flex-1 rounded-2xl border border-slate-100 bg-slate-50 p-6 dark:border-slate-800 dark:bg-slate-950/40">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">Preview</h2>
            {previewData.length === 0 ? (
              <p className="mt-4 text-sm text-slate-500 dark:text-slate-400">Upload a CSV to see the first few rows here.</p>
            ) : (
              <div className="mt-4 overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-900">
                <table className="min-w-full divide-y divide-slate-200 text-sm dark:divide-slate-800">
                  <thead className="bg-slate-100 dark:bg-slate-800">
                    <tr>
                      {previewHeaders.map((header) => (
                        <th key={header} className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-300">
                          {header}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                    {previewData.map((row, rowIndex) => (
                      <tr key={row.unique_id || rowIndex}>
                        {previewHeaders.map((header) => (
                          <td key={`${rowIndex}-${header}`} className="px-3 py-2 text-slate-700 dark:text-slate-200">
                            {row[header]}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </section>

      {(result.total > 0 || result.errors.length > 0) && (
        <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-900">
          <h2 className="text-lg font-semibold text-slate-900 dark:text-white">Import summary</h2>
          <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <div className="rounded-xl border border-slate-100 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-800/30">
              <p className="text-xs uppercase text-slate-500 dark:text-slate-400">Processed</p>
              <p className="mt-2 text-2xl font-semibold text-slate-900 dark:text-white">{result.total}</p>
            </div>
            <div className="rounded-xl border border-slate-100 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-800/30">
              <p className="text-xs uppercase text-emerald-500">Added</p>
              <p className="mt-2 text-2xl font-semibold text-emerald-500">{result.added}</p>
            </div>
            <div className="rounded-xl border border-slate-100 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-800/30">
              <p className="text-xs uppercase text-slate-500 dark:text-slate-400">Skipped</p>
              <p className="mt-2 text-2xl font-semibold text-slate-700 dark:text-slate-200">{result.skipped}</p>
            </div>
            <div className="rounded-xl border border-slate-100 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-800/30">
              <p className="text-xs uppercase text-brand">Assigned mentors</p>
              <p className="mt-2 text-2xl font-semibold text-brand">{result.assigned}</p>
            </div>
          </div>

          {result.errors.length > 0 && (
            <div className="mt-4 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700 dark:border-rose-400/40 dark:bg-rose-500/10 dark:text-rose-200">
              <p className="font-semibold">Issues encountered:</p>
              <ul className="mt-2 space-y-1 pl-5">
                {result.errors.map((error, index) => (
                  <li key={index} className="list-disc">
                    {error}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </section>
      )}

      <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-900">
        <h2 className="text-lg font-semibold text-slate-900 dark:text-white">CSV template</h2>
        <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">
          Ensure your CSV looks like the example below before uploading.
        </p>
        <div className="mt-4 overflow-x-auto rounded-xl border border-slate-200 bg-slate-50 dark:border-slate-700 dark:bg-slate-950/40">
          <table className="min-w-full divide-y divide-slate-200 text-sm dark:divide-slate-800">
            <thead className="bg-slate-100 dark:bg-slate-800">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-300">
                  Student Name
                </th>
                <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-300">
                  EPS ID
                </th>
                <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-300">
                  Student Number
                </th>
                <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-300">
                  Student Whatsapp
                </th>
                <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-300">
                  Parent Number
                </th>
                <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-300">
                  Course
                </th>
                <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-300">
                  Campus
                </th>
                <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-300">
                  Hostel
                </th>
                <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-300">
                  Created At
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              <tr>
                <td className="px-3 py-2">Alex Johnson</td>
                <td className="px-3 py-2">EPS001</td>
                <td className="px-3 py-2">+1 800 555 0100</td>
                <td className="px-3 py-2">+1 800 555 0101</td>
                <td className="px-3 py-2">+1 800 555 0102</td>
                <td className="px-3 py-2">JEE - Integrated 26-28</td>
                <td className="px-3 py-2">Main Campus</td>
                <td className="px-3 py-2">Block A</td>
                <td className="px-3 py-2">2024-01-15</td>
              </tr>
              <tr>
                <td className="px-3 py-2">Priya Singh</td>
                <td className="px-3 py-2">EPS002</td>
                <td className="px-3 py-2">+91 98765 43210</td>
                <td className="px-3 py-2">+91 98765 43211</td>
                <td className="px-3 py-2">+91 98765 43212</td>
                <td className="px-3 py-2">NEET Integrated 26-28</td>
                <td className="px-3 py-2">North Campus</td>
                <td className="px-3 py-2">Block B</td>
                <td className="px-3 py-2">2024-01-16</td>
              </tr>
            </tbody>
          </table>
        </div>
        <a
          href="/sample-students.csv"
          download
          className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-brand hover:text-brand-dark"
        >
          <span>Download sample CSV</span>
          <span aria-hidden="true">→</span>
        </a>
      </section>
    </div>
  );
}
